import { Tooltip } from '@mui/material'
import { useDatasets } from 'api/dataset/dataset.api'
import { useDigitalTwin } from 'api/digitalTwin/digitalTwin.api'
import {
  useCopyBidsFromDate,
  useElplanBidCreateMutation,
  useElplanInfo,
  useElplanSendingBidMutation,
  useSentBidsToBroker,
} from 'api/elplan/elplan.api'
import { resolveElplanBidsQueryEndTime } from 'api/elplan/elplan.bidQueryWindow.helper'
import { ELPLAN_BID_REFRESH_EVENT_QUERY_KEY } from 'api/elplan/elplan.constants'
import { BidTypeName } from 'api/elplan/elplan.types'
import { postStat } from 'api/stats/stats.api'
import { useUiConfigAnchorComponent } from 'api/uiConfig/uiConfig.api'
import { getCalculatedDataset } from 'helpers/dataset.helper/dataset.calc.helper'
import { exportPlannedElectricityProductionToExcel } from 'helpers/elplan.helper/elplanExport.helper'
import { convertNumberDecimalSign, isArray } from 'helpers/global.helper/global.helper'
import i18n from 'i18next'
import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import elplanStore, { type ElplanDateView, setCurrentDate, setCurrentDateView } from 'store/elplan/elplan'
import { colors } from 'styles/variables'
import { Button, Card, Switch, Tabs } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import TooltipIconButton from 'ui/components/TooltipIconButton/TooltipIconButton'
import { DatePickerInForm } from 'ui/molecules/pickers/DatePicker/DatePicker'
import Datetime from 'utils/datetime/datetime'
import { translateKeyWithSpecificTranslations } from 'utils/stringUtils'
import { useSnapshot } from 'valtio'
import {
  FormattedBid,
  FormattedBidWithIdAndTitle,
  getDatasetInstructionsForSumVolumeCalc,
  getItemsForExcel,
} from '../DayAheadBidManagement/DayAheadBidManagement.helper'
import BidModal from '../shared/DayAheadBidEditor/BidModal/BidModal'
import CopyBidsModal from '../shared/DayAheadBidEditor/CopyBidsModal/CopyBidsModal'
import { useDayAheadBidTagSelectionContext } from '../shared/DayAheadBidEditor/useDayAheadBidTagSelectionContext'
import styles from './DayAheadPriceScenarios.module.less'
import ElectricityPriceScenarioModal from './ElectricityPriceScenarioModal/ElectricityPriceScenarioModal'
import ElectricityPriceScenarioVisualization from './ElectricityPriceScenarioVisualization/ElectricityPriceScenarioVisualization'

const showSentBidsAtBanner = (sentBidsToBrokerAtDate: undefined | { latest_date: ISODateTime }): ReactElement => {
  if (sentBidsToBrokerAtDate?.latest_date) {
    const formattedDate = Datetime.toLocalTime(sentBidsToBrokerAtDate.latest_date, 'hour')
    return (
      <InfoBanner
        style={'info'}
        variant="text"
        text={i18n.t(`Bids sent at {{sentDate}}.`, {
          sentDate: formattedDate,
        })}
      />
    )
  }

  return <InfoBanner style={'warning'} variant="text" text={i18n.t('No bids sent')} />
}

export function DayAheadPriceScenarios() {
  const { t } = useTranslation()

  const auth = useAuth()
  const elplanSnap = useSnapshot(elplanStore)
  const { data: elplanInfo, isPending } = useElplanInfo(auth.systemId)
  const { tagsForDate, requireTagForBlockBids, blockBidInputCurrency, profileBidInputCurrency } =
    useDayAheadBidTagSelectionContext()

  const { mutate: createBid } = useElplanBidCreateMutation()
  const { mutate: copyBidsFromDate } = useCopyBidsFromDate()
  const { mutate: sendBidToElplan } = useElplanSendingBidMutation()

  const primaryDigitalTwin = auth.activeSystem?.primary_digital_twin
  const { data: digitalTwin } = useDigitalTwin(primaryDigitalTwin?.uid)

  const [typeOfBidToCopy, setTypeOfBidToCopy] = useState<BidTypeName>()
  const [currentBlockBid, setCurrentBlockBid] = useState<FormattedBidWithIdAndTitle>()
  const [currentProfileBid, setCurrentProfileBid] = useState<FormattedBidWithIdAndTitle>()
  const [openElectricityPriceModal, setOpenElectricityPriceModal] = useState(false)

  const selectedStartTime = elplanSnap.date.start_time ?? Datetime.getTomorrowDate().startTime
  const selectedEndTime = elplanSnap.date.end_time ?? Datetime.getTomorrowDate().endTime
  const selectedDate = {
    startTime: selectedStartTime,
    endTime: selectedEndTime,
  }

  const currentView = elplanSnap.date.current_view ?? 'tomorrow'

  const isCustomDate = currentView === 'custom'
  const disabledBidButtons = currentView === 'optimizationPeriod'

  const systemHasBlockBids = elplanSnap.bid_types.some((bid) => bid.type === 'blockbid')
  const systemHasProfileBids = elplanSnap.bid_types.some((bid) => bid.type === 'profilebid')
  const amountOfBlockBids = elplanSnap.blockBidInfo.amount || 0
  const amountOfProfileBids = elplanSnap.profileBidInfo.amount || 0

  const sentBidsToBrokerAtDate = useSentBidsToBroker(
    elplanSnap.elplan_info_id,
    selectedStartTime,
    selectedEndTime,
    undefined
  )

  // Datasets for excel export of bids
  const onlyReturnIds = useMemo(() => {
    const onlyReturnIds = ['spotbid_without_price.volume']

    if (systemHasBlockBids) {
      for (let i = 0; i < amountOfBlockBids; i++) {
        onlyReturnIds.push(`blockbid.${i + 1}.volume`)
        onlyReturnIds.push(`blockbid.${i + 1}.price`)
      }
    }

    if (systemHasProfileBids) {
      for (let i = 0; i < amountOfProfileBids; i++) {
        onlyReturnIds.push(`profilebid.${i + 1}.volume`)
        onlyReturnIds.push(`profilebid.${i + 1}.price`)
      }
    }

    return onlyReturnIds
  }, [amountOfBlockBids, amountOfProfileBids, systemHasBlockBids, systemHasProfileBids])

  const { data: elplanBidsUiConfig } = useUiConfigAnchorComponent('elplan_bids')
  const { data: elplanBidsDatasets = [] } = useDatasets(
    elplanBidsUiConfig?.id ?? -1,
    elplanBidsUiConfig?.version ?? -1,
    elplanBidsUiConfig?.dataset_instructions || [],
    selectedStartTime,
    resolveElplanBidsQueryEndTime(selectedEndTime),
    {
      onlyReturnIds,
      fillMissingHourTimestamps: false,
      ensureUniformTimeIntervals: false,
      datasetRefreshToken: ELPLAN_BID_REFRESH_EVENT_QUERY_KEY,
      overrideAlias: {
        elplan_info_id: elplanSnap.elplan_info_id,
      },
    }
  )

  const calcDatasets = getCalculatedDataset(
    elplanBidsDatasets,
    getDatasetInstructionsForSumVolumeCalc(amountOfBlockBids)
  )
  const maxBidVolume = elplanSnap?.maxBidVolume
  const bidsIsOvermaxBidVolume =
    typeof maxBidVolume === 'number' ? calcDatasets.values.some((num) => num && num > maxBidVolume) : false

  const updateCurrentView = useCallback((newCurrentView: ElplanDateView) => {
    if (newCurrentView === 'tomorrow') {
      setCurrentDate(Datetime.getTomorrowDate().startTime, Datetime.getTomorrowDate().endTime)
      return
    }

    if (newCurrentView === 'optimizationPeriod') {
      setCurrentDate(Datetime.getTodayDate().startTime, Datetime.getDateOneWeekAhead())
      return
    }

    if (newCurrentView === 'custom') {
      setCurrentDateView('custom')
      return
    }
  }, [])

  const itemsForExcel = getItemsForExcel(amountOfBlockBids)
  const itemsDataId: Set<string> = new Set(itemsForExcel.map((item) => item.data_id))
  const visibleDatasets = elplanBidsDatasets.filter(({ return_id }) => itemsDataId.has(return_id))
  const datasetNames = (itemsForExcel || []).reduce((acc, item) => ({ ...acc, [item.data_id]: item.title }), {})

  function onDateTimePickerChange(value: ISODateTime): void {
    setCurrentDate(Datetime.getStartOfDay(value), Datetime.getEndOfDay(value))
  }

  function getBidVolumeWithConvertedDecimalSign(bid: FormattedBid): number | number[] | undefined {
    if (isArray(bid.volume)) {
      return bid.volume.map((vol) => convertNumberDecimalSign(vol))
    }
    if (typeof bid.volume === 'number') {
      return convertNumberDecimalSign(bid.volume)
    }
    return bid.volume
  }

  function addNewBlockOrProfileBid(bid: FormattedBid): void {
    createBid({
      elplan_info: elplanSnap.elplan_info_id,
      tag: bid.tag,
      start_time: Datetime.toISOString(bid.startTime),
      end_time: Datetime.toISOString(bid.endTime),
      type: bid.type,
      volume: getBidVolumeWithConvertedDecimalSign(bid),
      price: bid.price ? convertNumberDecimalSign(bid.price) : undefined,
      name: bid.name || '-',
      electricity_price_scenario: bid.scenarioId,
      generate_block_order: true,
    })
  }

  function copySpotBidsFromDate(date: ISODateTime, type: string): void {
    postStat('copy-spotbids-from-date', `click`)

    setTypeOfBidToCopy(undefined)
    copyBidsFromDate({
      elplan_info: elplanSnap.elplan_info_id,
      from_date: Datetime.toLocalTime(date, 'onlyDate') as ISODateTime,
      to_date: Datetime.toLocalTime(selectedStartTime, 'onlyDate') as ISODateTime,
      type: type,
    })
  }

  function closeBlockBidModal(): void {
    setCurrentBlockBid(undefined)
  }

  function closeProfileBidModal(): void {
    setCurrentProfileBid(undefined)
  }

  if (!elplanInfo || isPending) {
    return null
  }
  if (elplanInfo.scenarios.length === 0) {
    return (
      <Card title={t(`Day ahead price scenarios`)}>
        <InfoBanner
          style={'warning'}
          text={t('No electricity price scenarios found. Please contact support to set up scenarios for your system.')}
        />
      </Card>
    )
  }

  return (
    <>
      <div>
        <Card title={t(`Day ahead price scenarios`)}>
          <div className={styles.DayAheadPriceScenarios_Period}>
            <Switch
              value={currentView}
              items={[
                { label: t('Custom'), value: 'custom' },
                { label: t('Week'), value: 'optimizationPeriod' },
                { label: t('Tomorrow'), value: 'tomorrow' },
              ]}
              onClick={(value) => {
                updateCurrentView(value as ElplanDateView)
              }}
              // The shared store can hold views this switch has no item for (e.g. 'today'); show no active tab rather than defaulting to Custom.
              setNoTabActive={!['custom', 'optimizationPeriod', 'tomorrow'].includes(currentView)}
            />
            {isCustomDate && (
              <DatePickerInForm
                value={selectedStartTime}
                onSubmit={onDateTimePickerChange}
                label={t('Chosen date')}
                showWeekNumbers
                granularity={'day'}
                isRequired
              />
            )}
            <div className={styles.DayAheadPriceScenarios_Period__right}>
              {elplanInfo.integration && (
                <Tooltip
                  title={
                    bidsIsOvermaxBidVolume
                      ? t(`Some bids are over the maximum bid volume. Max volume for bids is {{maxBidVolume}} MW`, {
                          maxBidVolume: elplanSnap.maxBidVolume,
                        })
                      : ''
                  }
                  arrow
                >
                  <div className={styles.DayAheadPriceScenarios_SendBidsButtonContainer}>
                    <Button
                      primary
                      marginLeft
                      disabled={bidsIsOvermaxBidVolume || currentView === 'optimizationPeriod'}
                      onClick={() => {
                        if (!auth.activeSystem) {
                          return
                        }
                        sendBidToElplan({
                          date: Datetime.toLocalTime(selectedStartTime, 'onlyDate'),
                          system: auth.activeSystem?.name,
                          systemId: auth.activeSystem.id,
                        })
                      }}
                    >
                      {t('Send bids')}
                    </Button>
                    {currentView !== 'optimizationPeriod' &&
                      showSentBidsAtBanner(sentBidsToBrokerAtDate.data ?? undefined)}
                  </div>
                </Tooltip>
              )}
              <TooltipIconButton
                icon="fas fa-cloud-download-alt"
                iconColor={colors.primary5}
                iconSize={'medium'}
                onClick={() => exportPlannedElectricityProductionToExcel(visibleDatasets, datasetNames, selectedDate)}
                tooltip={t('Export to Excel with 15 minute resolution')}
              />
            </div>
            {elplanInfo.scenarios.length > 1 && (
              <Button
                className={styles.DayAheadPriceScenarios_ElectricityPriceButton}
                secondary
                icon="fal fa-plus"
                onClick={() => setOpenElectricityPriceModal(true)}
              >
                {t('Add electricity price for scenarios')}
              </Button>
            )}
          </div>
          {elplanInfo.scenarios.length === 1 && (
            <ElectricityPriceScenarioVisualization
              scenario={elplanInfo.scenarios[0]}
              datasetStartTime={selectedStartTime}
              datasetEndTime={selectedEndTime}
              setCurrentBlockBid={setCurrentBlockBid}
              setCurrentProfileBid={setCurrentProfileBid}
              setTypeOfBidToCopy={setTypeOfBidToCopy}
              disabledBidButtons={disabledBidButtons}
              tagsForDate={tagsForDate}
              bidInputCurrencies={{ block: blockBidInputCurrency, profile: profileBidInputCurrency }}
              requireTagForBlockBids={requireTagForBlockBids}
              translations={digitalTwin?.translations}
            />
          )}
          {(elplanInfo?.scenarios?.length ?? 0) > 1 && (
            <Tabs
              items={(elplanInfo?.scenarios ?? [])
                .sort((a, b) => a.scenario_number - b.scenario_number)
                .map((scenario) => ({
                  title: translateKeyWithSpecificTranslations(scenario.scenario_name, digitalTwin?.translations),
                  children: (
                    <ElectricityPriceScenarioVisualization
                      scenario={scenario}
                      datasetStartTime={selectedStartTime}
                      datasetEndTime={selectedEndTime}
                      setCurrentBlockBid={setCurrentBlockBid}
                      setCurrentProfileBid={setCurrentProfileBid}
                      setTypeOfBidToCopy={setTypeOfBidToCopy}
                      disabledBidButtons={disabledBidButtons}
                      tagsForDate={tagsForDate}
                      bidInputCurrencies={{ block: blockBidInputCurrency, profile: profileBidInputCurrency }}
                      requireTagForBlockBids={requireTagForBlockBids}
                      translations={digitalTwin?.translations}
                    />
                  ),
                }))}
              uniqueStorageKey={'elprice_scenario_tabs'}
            />
          )}
        </Card>
        {typeOfBidToCopy !== undefined && (
          <CopyBidsModal
            onSubmit={(chosenDate) => copySpotBidsFromDate(chosenDate, typeOfBidToCopy ?? '')}
            closeModal={() => setTypeOfBidToCopy(undefined)}
          />
        )}
        {currentBlockBid !== undefined && (
          <BidModal
            closeModal={closeBlockBidModal}
            bid={currentBlockBid}
            createBid={addNewBlockOrProfileBid}
            editBid={addNewBlockOrProfileBid}
            type={'block'}
            isEditing={currentBlockBid.fake}
            tagsForDate={tagsForDate}
            requireTagForBlockBids={requireTagForBlockBids}
            bidInputCurrency={blockBidInputCurrency}
          />
        )}
        {currentProfileBid !== undefined && (
          <BidModal
            closeModal={closeProfileBidModal}
            bid={currentProfileBid}
            createBid={addNewBlockOrProfileBid}
            editBid={addNewBlockOrProfileBid}
            type={'profile'}
            isEditing={currentProfileBid.fake}
            tagsForDate={tagsForDate}
            requireTagForBlockBids={requireTagForBlockBids}
            bidInputCurrency={profileBidInputCurrency}
          />
        )}
      </div>
      {openElectricityPriceModal && (
        <ElectricityPriceScenarioModal
          scenarios={elplanInfo?.scenarios ?? []}
          chosenDate={selectedDate}
          closeModal={() => setOpenElectricityPriceModal(false)}
        />
      )}
    </>
  )
}
